import posthog from 'posthog-js';
import { AnalyticsProvider, AnalyticsConfig, AnalyticsUser } from '../types';

export class PostHogAnalyticsProvider implements AnalyticsProvider {
  private isInitialized = false;
  private isDisabled = false;

  init(config: AnalyticsConfig): void {
    if (this.isInitialized) return;

    if (process.env.REACT_APP_DISABLE_ANALYTICS === 'true') {
      this.isDisabled = true;
      this.isInitialized = true;
      // eslint-disable-next-line no-console
      console.info('[analytics] disabled via REACT_APP_DISABLE_ANALYTICS');
      return;
    }

    posthog.init(config.apiKey, {
      api_host: config.host || 'https://eu.i.posthog.com',
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      enable_recording_console_log: config.debug,
      enable_heatmaps: config.enableHeatmaps ?? true,
      loaded: (ph) => {
        if (config.debug) {
          ph.debug();
        }
      },
    });

    posthog.register({
      app: config.app,
      environment: config.environment,
    });

    if (config.enableRecording) {
      posthog.startSessionRecording();
    }

    this.isInitialized = true;
  }

  identify(user: AnalyticsUser): void {
    if (this.isDisabled) return;
    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      is_admin: user.isAdmin,
      position: user.position,
      profession: user.profession,
      tenant_id: user.tenantId,
    });
  }

  reset(): void {
    if (this.isDisabled) return;
    posthog.reset();
  }

  track(event: string, properties?: Record<string, unknown>): void {
    if (this.isDisabled) return;
    posthog.capture(event, properties);
  }

  pageView(path: string, properties?: Record<string, unknown>): void {
    if (this.isDisabled) return;
    posthog.capture('$pageview', { $current_url: path, ...properties });
  }

  isFeatureEnabled(flag: string): boolean | undefined {
    if (this.isDisabled) return undefined;
    return posthog.isFeatureEnabled(flag) ?? undefined;
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    if (this.isDisabled) return undefined;
    return posthog.getFeatureFlag(flag) ?? undefined;
  }

  onFeatureFlagsLoaded(callback: () => void): void {
    if (this.isDisabled) return;
    posthog.onFeatureFlags(callback);
  }

  startSessionRecording(): void {
    if (this.isDisabled) return;
    posthog.startSessionRecording();
  }

  stopSessionRecording(): void {
    if (this.isDisabled) return;
    posthog.stopSessionRecording();
  }
}
